import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import LocaleMemories from '@/components/LocaleMemories';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  MapPin, Calendar, Clock, Search, Filter, X, Plus, 
  CalendarDays, Ticket, Tag, Star, Heart, Bookmark,
  Music, Utensils, Trophy, Palette, Moon, ShoppingBag,
  Dumbbell, BookOpen, Gamepad2, Users, Mountain, HeartHandshake,
  BadgeCheck, ExternalLink, Navigation, List, Map as MapIcon,
  ChevronDown, Sparkles, DollarSign, Globe, ArrowLeft, Target
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category icons mapping
const categoryIcons = {
  music: Music,
  food: Utensils,
  sports: Trophy,
  art: Palette,
  nightlife: Moon,
  shopping: ShoppingBag,
  fitness: Dumbbell,
  education: BookOpen,
  tech: Gamepad2,
  family: Users,
  outdoor: Mountain,
  community: HeartHandshake
};

// Post type colors
const typeColors = {
  event: 'bg-blue-500',
  show: 'bg-purple-500',
  special: 'bg-orange-500',
  deal: 'bg-green-500'
};

const typeLabels = {
  event: 'Event',
  show: 'Show',
  special: 'Special',
  deal: 'Deal'
};

const Locale = ({ user }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  
  const [posts, setPosts] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    post_type: 'event',
    address: '',
    latitude: 40.7128,
    longitude: -74.0060,
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    image_url: '',
    price: '',
    category: '',
    tags: '',
    website_url: '',
    ticket_url: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Categories list
  const categories = [
    { id: 'music', label: 'Music & Concerts', icon: Music },
    { id: 'food', label: 'Food & Dining', icon: Utensils },
    { id: 'sports', label: 'Sports', icon: Trophy },
    { id: 'art', label: 'Art & Culture', icon: Palette },
    { id: 'nightlife', label: 'Nightlife', icon: Moon },
    { id: 'shopping', label: 'Shopping', icon: ShoppingBag },
    { id: 'fitness', label: 'Fitness & Wellness', icon: Dumbbell },
    { id: 'education', label: 'Education', icon: BookOpen },
    { id: 'tech', label: 'Tech & Gaming', icon: Gamepad2 },
    { id: 'family', label: 'Family & Kids', icon: Users },
    { id: 'outdoor', label: 'Outdoor', icon: Mountain },
    { id: 'community', label: 'Community', icon: HeartHandshake }
  ];

  // Check if user can create locale posts (Premium Business + Admins only)
  const canCreatePost = (user?.is_admin) || 
    ((user?.subscription_tier === 'creator' || user?.subscription_tier === 'pro' || user?.subscription_tier === 'premium') && 
     (user?.is_business || user?.account_type === 'business'));

  useEffect(() => {
    fetchPosts();
  }, [selectedType, selectedCategory, searchQuery]);

  useEffect(() => {
    if (viewMode === 'map') {
      initMap();
      fetchMapMarkers();
    }
    return () => {
      // Cleanup map
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [viewMode]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('post_type', selectedType);
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_URL}/locale/?${params.toString()}`, { withCredentials: true });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch locale posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchMapMarkers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('post_type', selectedType);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API_URL}/locale/map?${params.toString()}`, { withCredentials: true });
      setMarkers(response.data.markers || []);
      updateMapMarkers(response.data.markers || []);
    } catch (error) {
      console.error('Failed to fetch markers');
    }
  };

  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;
    
    // Load Leaflet CSS dynamically
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    
    // Load Leaflet JS dynamically
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => createMap();
      document.body.appendChild(script);
    } else {
      createMap();
    }
  };

  const createMap = () => {
    if (!mapRef.current || !window.L) return;
    
    const map = window.L.map(mapRef.current).setView([40.7128, -74.0060], 12);
    
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    
    mapInstanceRef.current = map;
    
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          map.setView([position.coords.latitude, position.coords.longitude], 13);
        },
        () => {
          // Default to NYC if geolocation fails
        }
      );
    }
    
    // Fetch markers after map is created
    fetchMapMarkers();
  };

  const updateMapMarkers = (newMarkers) => {
    if (!mapInstanceRef.current || !window.L) return;
    
    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    
    // Add new markers
    newMarkers.forEach(post => {
      const color = typeColors[post.post_type]?.replace('bg-', '') || 'blue';
      
      const markerIcon = window.L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            width: 32px; 
            height: 32px; 
            background: ${color === 'blue-500' ? '#3b82f6' : color === 'purple-500' ? '#a855f7' : color === 'orange-500' ? '#f97316' : '#22c55e'};
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            border: 3px solid white;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <span style="transform: rotate(45deg); color: white; font-size: 14px;">★</span>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      });
      
      const marker = window.L.marker([post.latitude, post.longitude], { icon: markerIcon })
        .addTo(mapInstanceRef.current)
        .on('click', () => {
          fetchPostDetails(post.locale_id);
        });
      
      marker.bindPopup(`
        <div style="min-width: 200px;">
          <strong>${post.title}</strong><br/>
          <span style="color: #666;">${post.business_name}</span><br/>
          <small>${post.address}</small>
        </div>
      `);
      
      markersRef.current.push(marker);
    });
  };

  const fetchPostDetails = async (localeId) => {
    try {
      const response = await axios.get(`${API_URL}/locale/${localeId}`, { withCredentials: true });
      setSelectedPost(response.data);
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const createLocalePost = async () => {
    if (!createForm.title || !createForm.description || !createForm.address) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setCreating(true);
    try {
      const postData = {
        ...createForm,
        tags: createForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      await axios.post(`${API_URL}/locale/`, postData, { withCredentials: true });
      toast.success('Locale post created!');
      setShowCreateModal(false);
      setCreateForm({
        title: '', description: '', post_type: 'event', address: '',
        latitude: 40.7128, longitude: -74.0060, start_date: '', end_date: '',
        start_time: '', end_time: '', image_url: '', price: '', category: '',
        tags: '', website_url: '', ticket_url: ''
      });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const toggleInterested = async (localeId) => {
    try {
      const response = await axios.post(`${API_URL}/locale/${localeId}/interested`, {}, { withCredentials: true });
      setPosts(posts.map(p => 
        p.locale_id === localeId 
          ? { ...p, is_interested: response.data.interested, interested_count: p.interested_count + (response.data.interested ? 1 : -1) }
          : p
      ));
      if (selectedPost?.locale_id === localeId) {
        setSelectedPost({ ...selectedPost, is_interested: response.data.interested });
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleSave = async (localeId) => {
    try {
      const response = await axios.post(`${API_URL}/locale/${localeId}/save`, {}, { withCredentials: true });
      setPosts(posts.map(p => 
        p.locale_id === localeId 
          ? { ...p, is_saved: response.data.saved }
          : p
      ));
      if (selectedPost?.locale_id === localeId) {
        setSelectedPost({ ...selectedPost, is_saved: response.data.saved });
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const PostCard = ({ post }) => {
    const CategoryIcon = categoryIcons[post.category] || MapPin;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow cursor-pointer"
        onClick={() => setSelectedPost(post)}
        data-testid={`locale-post-${post.locale_id}`}
      >
        {post.image_url && (
          <div className="relative h-40">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
            <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white ${typeColors[post.post_type]}`}>
              {typeLabels[post.post_type]}
            </div>
          </div>
        )}
        
        {!post.image_url && (
          <div className={`h-24 ${typeColors[post.post_type]} flex items-center justify-center`}>
            <CategoryIcon className="w-10 h-10 text-white/80" />
          </div>
        )}
        
        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-bold text-gray-900 dark:text-white line-clamp-2">{post.title}</h3>
            {post.price && (
              <span className="text-sm font-semibold text-green-600 whitespace-nowrap">{post.price}</span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Avatar className="w-5 h-5">
              <AvatarImage src={post.user?.avatar} />
              <AvatarFallback className="text-xs">{post.business_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="truncate">{post.business_name}</span>
            {post.user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
          </div>
          
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{post.address}</span>
          </div>
          
          {post.start_date && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{format(new Date(post.start_date), 'MMM d, yyyy')}</span>
              {post.start_time && <span>at {post.start_time}</span>}
            </div>
          )}
          
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                {post.interested_count || 0}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); toggleInterested(post.locale_id); }}
                className={`p-2 rounded-full transition-colors ${post.is_interested ? 'bg-yellow-100 text-yellow-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Star className={`w-4 h-4 ${post.is_interested ? 'fill-yellow-500' : ''}`} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); toggleSave(post.locale_id); }}
                className={`p-2 rounded-full transition-colors ${post.is_saved ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Bookmark className={`w-4 h-4 ${post.is_saved ? 'fill-blue-500' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-primary" />
                  Locale
                </h1>
                <p className="text-sm text-gray-500">Discover events, shows, specials & deals near you</p>
              </div>
            </div>
            
            {canCreatePost && (
              <div className="flex items-center gap-2">
                {(user?.subscription_tier === 'supremium' || user?.is_admin) && (
                  <Button
                    onClick={() => navigate('/locale/advanced')}
                    variant="outline"
                    className="rounded-full border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                    data-testid="advanced-locale-btn"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Advanced
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/locale/payments')}
                  variant="outline"
                  className="rounded-full border-green-400 text-green-600 hover:bg-green-50"
                  data-testid="locale-payments-btn"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Payments
                </Button>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold"
                  data-testid="create-locale-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create
                </Button>
              </div>
            )}
          </div>
          
          {/* Search and Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search events, places, businesses..."
                className="pl-10 rounded-full border-2 h-11"
                data-testid="locale-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-full ${showFilters ? 'bg-primary text-white' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <div className="flex rounded-full border-2 overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 flex items-center gap-1 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-2 flex items-center gap-1 transition-colors ${viewMode === 'map' ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                data-testid="map-view-btn"
              >
                <MapIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Filter Options */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 space-y-3">
                  {/* Type Filter */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Type:</span>
                    {['event', 'show', 'special', 'deal'].map(type => (
                      <button
                        key={type}
                        onClick={() => setSelectedType(selectedType === type ? null : type)}
                        className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
                          selectedType === type 
                            ? `${typeColors[type]} text-white` 
                            : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {typeLabels[type]}
                      </button>
                    ))}
                  </div>
                  
                  {/* Category Filter */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Category:</span>
                    {categories.slice(0, 8).map(cat => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                          className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${
                            selectedCategory === cat.id 
                              ? 'bg-primary text-white' 
                              : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Icon className="w-3 h-3" />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {viewMode === 'list' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl h-64 animate-pulse" />
              ))
            ) : posts.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-600 dark:text-gray-400">No locale posts found</h3>
                <p className="text-gray-500">Try adjusting your filters or search query</p>
              </div>
            ) : (
              posts.map(post => <PostCard key={post.locale_id} post={post} />)
            )}
          </div>
        ) : (
          <div className="h-[calc(100vh-250px)] rounded-2xl overflow-hidden shadow-xl">
            <div ref={mapRef} className="w-full h-full" id="locale-map" />
          </div>
        )}
      </div>

      {/* Post Detail Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
              data-testid="locale-detail-modal"
            >
              {selectedPost.image_url && (
                <div className="relative h-48">
                  <img src={selectedPost.image_url} alt={selectedPost.title} className="w-full h-full object-cover" />
                  <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold text-white ${typeColors[selectedPost.post_type]}`}>
                    {typeLabels[selectedPost.post_type]}
                  </div>
                  <button
                    onClick={() => setSelectedPost(null)}
                    className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              
              <div className="p-6">
                {!selectedPost.image_url && (
                  <div className="flex justify-end mb-4">
                    <button onClick={() => setSelectedPost(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{selectedPost.title}</h2>
                
                <div className="flex items-center gap-2 mb-4">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={selectedPost.user?.avatar} />
                    <AvatarFallback>{selectedPost.business_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedPost.business_name}</span>
                  {selectedPost.user?.is_verified && <BadgeCheck className="w-5 h-5 text-blue-500" />}
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">{selectedPost.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>{selectedPost.address}</span>
                  </div>
                  
                  {selectedPost.start_date && (
                    <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                      <Calendar className="w-5 h-5 text-primary" />
                      <span>
                        {format(new Date(selectedPost.start_date), 'EEEE, MMMM d, yyyy')}
                        {selectedPost.end_date && selectedPost.end_date !== selectedPost.start_date && 
                          ` - ${format(new Date(selectedPost.end_date), 'MMMM d, yyyy')}`
                        }
                      </span>
                    </div>
                  )}
                  
                  {selectedPost.start_time && (
                    <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                      <Clock className="w-5 h-5 text-primary" />
                      <span>
                        {selectedPost.start_time}
                        {selectedPost.end_time && ` - ${selectedPost.end_time}`}
                      </span>
                    </div>
                  )}
                  
                  {selectedPost.price && (
                    <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-green-600">{selectedPost.price}</span>
                    </div>
                  )}
                </div>
                
                {selectedPost.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedPost.tags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-600 dark:text-gray-400">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => toggleInterested(selectedPost.locale_id)}
                    className={`flex-1 rounded-full font-bold ${
                      selectedPost.is_interested 
                        ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Star className={`w-4 h-4 mr-2 ${selectedPost.is_interested ? 'fill-white' : ''}`} />
                    {selectedPost.is_interested ? 'Interested' : 'Mark Interested'}
                  </Button>
                  <Button
                    onClick={() => toggleSave(selectedPost.locale_id)}
                    variant="outline"
                    className="rounded-full"
                  >
                    <Bookmark className={`w-4 h-4 ${selectedPost.is_saved ? 'fill-current' : ''}`} />
                  </Button>
                </div>
                
                {(selectedPost.website_url || selectedPost.ticket_url) && (
                  <div className="flex gap-3 mt-3">
                    {selectedPost.website_url && (
                      <a
                        href={selectedPost.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full rounded-full">
                          <Globe className="w-4 h-4 mr-2" />
                          Website
                        </Button>
                      </a>
                    )}
                    {selectedPost.ticket_url && (
                      <a
                        href={selectedPost.ticket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button className="w-full rounded-full bg-primary hover:bg-primary-700 text-white">
                          <Ticket className="w-4 h-4 mr-2" />
                          Get Tickets
                        </Button>
                      </a>
                    )}
                  </div>
                )}
                
                {/* Locale Memories Section */}
                <LocaleMemories 
                  localeId={selectedPost.locale_id}
                  isOwner={selectedPost.user_id === user?.user_id || user?.is_admin}
                  user={user}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Locale Post Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl p-6"
              data-testid="create-locale-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Locale Post</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Post Type */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Type *</label>
                  <div className="flex gap-2 flex-wrap">
                    {['event', 'show', 'special', 'deal'].map(type => (
                      <button
                        key={type}
                        onClick={() => setCreateForm({ ...createForm, post_type: type })}
                        className={`px-4 py-2 rounded-full font-semibold transition-colors ${
                          createForm.post_type === type 
                            ? `${typeColors[type]} text-white` 
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {typeLabels[type]}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Title *</label>
                  <Input
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    placeholder="Event or deal title"
                    className="rounded-xl"
                    data-testid="locale-title-input"
                  />
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Description *</label>
                  <Textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Tell people about this event or deal..."
                    className="rounded-xl resize-none"
                    rows={3}
                    data-testid="locale-description-input"
                  />
                </div>
                
                {/* Address */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Address *</label>
                  <Input
                    value={createForm.address}
                    onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                    placeholder="123 Main St, City, State"
                    className="rounded-xl"
                    data-testid="locale-address-input"
                  />
                </div>
                
                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Latitude</label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={createForm.latitude}
                      onChange={(e) => setCreateForm({ ...createForm, latitude: parseFloat(e.target.value) })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Longitude</label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={createForm.longitude}
                      onChange={(e) => setCreateForm({ ...createForm, longitude: parseFloat(e.target.value) })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Start Date</label>
                    <Input
                      type="date"
                      value={createForm.start_date}
                      onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">End Date</label>
                    <Input
                      type="date"
                      value={createForm.end_date}
                      onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Start Time</label>
                    <Input
                      type="time"
                      value={createForm.start_time}
                      onChange={(e) => setCreateForm({ ...createForm, start_time: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">End Time</label>
                    <Input
                      type="time"
                      value={createForm.end_time}
                      onChange={(e) => setCreateForm({ ...createForm, end_time: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Category</label>
                  <select
                    value={createForm.category}
                    onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                    className="w-full p-3 rounded-xl border-2 bg-white dark:bg-gray-700"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Price */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Price</label>
                  <Input
                    value={createForm.price}
                    onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })}
                    placeholder="Free, $10, $25-50, etc."
                    className="rounded-xl"
                  />
                </div>
                
                {/* Image URL */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Image URL</label>
                  <Input
                    value={createForm.image_url}
                    onChange={(e) => setCreateForm({ ...createForm, image_url: e.target.value })}
                    placeholder="https://..."
                    className="rounded-xl"
                  />
                </div>
                
                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Tags (comma separated)</label>
                  <Input
                    value={createForm.tags}
                    onChange={(e) => setCreateForm({ ...createForm, tags: e.target.value })}
                    placeholder="music, live, weekend"
                    className="rounded-xl"
                  />
                </div>
                
                {/* Links */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Website</label>
                    <Input
                      value={createForm.website_url}
                      onChange={(e) => setCreateForm({ ...createForm, website_url: e.target.value })}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Ticket URL</label>
                    <Input
                      value={createForm.ticket_url}
                      onChange={(e) => setCreateForm({ ...createForm, ticket_url: e.target.value })}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={createLocalePost}
                  disabled={creating}
                  className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 mt-4"
                  data-testid="submit-locale-btn"
                >
                  {creating ? 'Creating...' : 'Create Locale Post'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default Locale;
